/** Reliability metrics page (spec §4.4): SAIDI/SAIFI/CAIDI from
 *  GET /oms/reliability/metrics (the compute job populates the table). Summary
 *  tiles for the latest system row + a full table + an admin CSV export. */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { isApiError } from '../../api/client.ts'
import { downloadOmsReport } from '../../api/oms.ts'
import { useReliabilityMetrics } from '../../hooks/useOms.ts'
import type { ReliabilityMetricItem } from '../../types/oms.ts'
import { Term } from '../common/Term.tsx'
import { formatDateTime } from './omsFormat.tsx'
import { OmsSubNav } from './OmsSubNav.tsx'

const PERIODS = ['day', 'month', 'quarter', 'year']

function num(v: number | null, digits = 2): string {
  return v === null || v === undefined ? '--' : v.toFixed(digits)
}

function Tile({ label, value, unit }: { label: ReactNode; value: string; unit?: string }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-2xl font-semibold text-gray-800">
        {value}
        {unit && <span className="text-sm text-gray-400 ml-1">{unit}</span>}
      </div>
    </div>
  )
}

export function ReliabilityMetricsPage() {
  const [period, setPeriod] = useState('month')
  const [scope, setScope] = useState('system')
  const [exportError, setExportError] = useState<string | null>(null)
  const { data, isLoading, isError } = useReliabilityMetrics({ period, scope, limit: 200 })

  const rows: ReliabilityMetricItem[] = data ?? []
  const latest = rows[0]

  async function onExport() {
    setExportError(null)
    try {
      await downloadOmsReport(
        `/oms/reliability/metrics?period=${period}&scope=${scope}&format=csv`,
        `oms_reliability_${period}_${scope}.csv`,
      )
    } catch (e) {
      setExportError(isApiError(e) ? e.detail : 'Export failed.')
    }
  }

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 space-y-4">
        <OmsSubNav />
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Reliability metrics</h2>
            <p className="text-sm text-gray-500">
              SAIDI / SAIFI / CAIDI, planned outages excluded. Computed nightly
              from the impact intervals.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={period}
              onChange={e => setPeriod(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
            >
              {PERIODS.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={scope}
              onChange={e => setScope(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
            >
              <option value="system">System</option>
              <option value="feeder">Feeder</option>
            </select>
            <button
              onClick={onExport}
              className="text-sm px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Export CSV
            </button>
          </div>
        </div>

        {exportError && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {exportError}
          </div>
        )}

        {latest && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label={<Term k="saidi">SAIDI</Term>} value={num(latest.saidi_minutes)} unit="min" />
            <Tile label={<Term k="saifi">SAIFI</Term>} value={num(latest.saifi_events, 3)} />
            <Tile label={<Term k="caidi">CAIDI</Term>} value={num(latest.caidi_minutes)} unit="min" />
            <Tile label="Customers" value={String(latest.total_customers)} />
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="text-sm text-gray-400 py-8 text-center">Loading metrics…</div>
          ) : isError ? (
            <div className="text-sm text-red-600 py-8 text-center">
              Failed to load reliability metrics.
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">
              No reliability metrics yet — the nightly compute job has not produced
              rows for this period/scope.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-500">
                  <tr className="border-b border-gray-200">
                    <th className="p-2">Date</th>
                    <th className="p-2">Period</th>
                    <th className="p-2">Scope</th>
                    <th className="p-2 text-right"><Term k="saidi">SAIDI</Term></th>
                    <th className="p-2 text-right"><Term k="saifi">SAIFI</Term></th>
                    <th className="p-2 text-right"><Term k="caidi">CAIDI</Term></th>
                    <th className="p-2 text-right"><Term k="cmi">CMI</Term></th>
                    <th className="p-2 text-right">Interruptions</th>
                    <th className="p-2">Calculated</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className="border-b border-gray-50">
                      <td className="p-2">{r.metric_date}</td>
                      <td className="p-2">{r.metric_period}</td>
                      <td className="p-2">
                        {r.scope}
                        {r.feeder_id != null ? ` #${r.feeder_id}` : ''}
                      </td>
                      <td className="p-2 text-right">{num(r.saidi_minutes)}</td>
                      <td className="p-2 text-right">{num(r.saifi_events, 3)}</td>
                      <td className="p-2 text-right">{num(r.caidi_minutes)}</td>
                      <td className="p-2 text-right">{r.customer_minutes_interrupted}</td>
                      <td className="p-2 text-right">{r.customer_interruptions}</td>
                      <td className="p-2 text-gray-400">{formatDateTime(r.calculated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
